import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiMagnifyingGlass, HiOutlineDocumentText, HiOutlineAcademicCap, HiOutlineArrowsUpDown } from "react-icons/hi2";
import { useSelector } from "react-redux";

import { CreateTrainingModal } from "../../components/CreateTrainingModal";
import { ReorderListModal } from "../../components/ReorderListModal";

import Loader from "@/components/loader";
import api from "@/services/api";

import placeholder from "@/assets/learn/placeholder.png";

export default function Course() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState();
  const [filter, setFilter] = useState({ search: "" });
  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    getTrainings();
  }, [filter]);

  const getTrainings = async () => {
    try {
      const { data, ok } = await api.post("/training/search", { title: filter.search });
      if (!ok) return toast.error("Error fetching trainings!");
      const sortedData = data.sort((a, b) => (a.position || 0) - (b.position || 0));
      setTrainings(sortedData);
    } catch (error) {
      console.error("Fetch error:", error);
      return toast.error("Error fetching trainings!");
    }
  };

  const handleReorderTrainings = async (reorderedTrainings) => {
    try {
      for (let newIndex = 0; newIndex < reorderedTrainings.length; newIndex++) {
        const training = reorderedTrainings[newIndex];
        if (training.position === newIndex) continue;

        const result = await api.put(`/training/${training._id}`, { position: newIndex });
        if (!result.ok) {
          throw new Error(`Failed to update position for training ${training._id}`);
        }
      }

      setTrainings(reorderedTrainings);
      toast.success("Training order updated successfully");
    } catch (error) {
      console.error("Error updating training order:", error);
      toast.error("Error updating training order!");
      await getTrainings();
    }
  };

  if (!trainings) return <Loader color="black" />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto p-6">
        <div className="flex items-center justify-between mb-8 gap-4 max-w-screen-2xl mx-auto">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Courses & Trainings</h1>
            <p className="text-gray-600">Discover and explore {trainings?.length || 0} comprehensive courses designed to enhance your skills and knowledge</p>
          </div>
          <div className="flex items-center justify-end gap-3">
            <div className="relative">
              <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                name="search"
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 w-64"
                placeholder="Search courses..."
              />
            </div>
            {["admin", "super_admin"].includes(user?.role) && (
              <div className="flex gap-2">
                <ReorderListModal items={trainings} onReorder={handleReorderTrainings}>
                  <button className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                    <HiOutlineArrowsUpDown className="w-4 h-4" />
                    Reorder
                  </button>
                </ReorderListModal>
                <CreateTrainingModal onDone={getTrainings} />
              </div>
            )}
          </div>
        </div>

        {trainings?.length === 0 ? (
          <div className="text-center py-16 max-w-screen-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
              <HiOutlineAcademicCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses found</h3>
              <p className="text-gray-500 mb-6">{filter.search ? `No courses match "${filter.search}"` : "Get started by creating your first course"}</p>
              {["admin", "super_admin"].includes(user?.role) && !filter.search && <CreateTrainingModal onDone={getTrainings} />}
            </div>
          </div>
        ) : (
          <div className="max-w-screen-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {trainings.map((training) => (
              <div
                key={training._id}
                onClick={() => navigate(`/selecole/course/${training._id}`)}
                className="group bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-gray-300 transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1 flex flex-col">
                <div className="relative w-full h-[200px] overflow-hidden">
                  <img
                    src={training.image || placeholder}
                    alt={training.image ? training.title : "Course placeholder"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">
                      {training.category || "TECH"}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">Course</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 group-hover:text-primary-700 transition-colors duration-200 line-clamp-2">{training.title}</h3>
                  {training.description && <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed flex-1">{training.description}</p>}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                    <div className="flex items-center text-xs text-gray-500">
                      <HiOutlineDocumentText className="w-4 h-4 mr-1.5 text-primary-500" />
                      <span className="font-medium">{training.updatedAt ? new Date(training.updatedAt).toLocaleDateString() : "Recently added"}</span>
                    </div>
                    <div className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">Start learning →</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
